import {Component, inject, OnInit} from '@angular/core';
import {CommonModule} from "@angular/common";
import {TableLazyLoadEvent, TableModule} from "primeng/table";
import {CardModule} from "primeng/card";
import {SelectChangeEvent, SelectModule} from "primeng/select";
import {FormsModule} from "@angular/forms";
import {ButtonModule} from "primeng/button";
import {TagModule} from "primeng/tag";
import {RouterLink} from "@angular/router";
import {BaseComponent} from "@app/core/bases";
import {StockListEbayFilters, StockService, UtilityService} from "@app/core/services";
import {StockListEbay} from "@app/core/interfaces";
import {debounceTime, Subject, takeUntil} from "rxjs";
import {InputNumberModule} from "primeng/inputnumber";
import {TooltipModule} from "primeng/tooltip";
import {SearchFilter} from "@app/features/product/product-filter/product-filter";

interface Column {
    field: string;
    header: string;
}

interface SortedStockListEbay {
    produit_id: number;
    produit_marque?: string | null;
    produit_reference?: string | null;
    photo_couverture?: string |null;
    stock_id: number;
    stock_etat: string | null;
    stock_prix: number | null;
    stock_quantite: number | null;
}

@Component({
  selector: 'app-ebay-search',
  imports: [
      CommonModule,
      TableModule,
      CardModule,
      InputNumberModule,
      SelectModule,
      FormsModule,
      ButtonModule,
      TagModule,
      TooltipModule,
      RouterLink,
  ],
  templateUrl: './ebay-search.html'
})
export class EbaySearch extends BaseComponent implements OnInit {
    utilityService: UtilityService = inject(UtilityService);
    stockService: StockService = inject(StockService);
    stockListEbays: StockListEbay[] = [];
    sortedStockListEbays: SortedStockListEbay[] = [];

    resultsLength = 0;
    pageIndex = 0;
    pageSize = 20;
    filters: StockListEbayFilters = {
        prix_min: 20,
        type: 1
    };
    cols: Column[] = [
        { field: 'produit_id', header: 'ID Produit' },
        { field: 'produit_marque', header: 'Marque' },
        { field: 'produit_reference', header: 'Référence' },
        { field: 'stock_etat', header: "État" },
        { field: 'stock_prix', header: 'Prix ' },
        { field: 'stock_quantite', header: 'Qte' },
    ];
    selectedColumns: Column[] = this.cols;

    prixSubject = new Subject<number>();

    isLoadingResults = true;
    isLoadedFromStorage = false;

    types: any[] = [
        {name: 'ID absent EBAY', value: 1},
        {name: 'État à rajouter EBAY', value: 2},
        {name: 'Vérification suppression EBAY', value: 3}
    ];

    constructor() {
        super();

        this.prixSubject.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe(value => this.onPriceChange(value));
    }

    ngOnInit(): void {
        const savedFilters = this.storageService.searchEbayFilter;
        if (savedFilters) {
            this.filters = { ...savedFilters } as StockListEbayFilters;
        }
    }

    sortEntries() {
        this.sortedStockListEbays = [];
        this.stockListEbays.forEach(item => {
            const product = item.produit;
            item.stocks.forEach(stock => {
                this.sortedStockListEbays.push({
                    produit_id: product.id,
                    produit_marque: product.marque?.nom,
                    produit_reference: product.reference,
                    photo_couverture: product.photo_couverture,
                    stock_id: stock.id,
                    stock_etat: stock.etat,
                    stock_prix: stock.prix,
                    stock_quantite: stock.quantite
                })
            })
        })
    }

    getTableEntries() {
        this.isLoadingResults = true;

        const formatedFilter: StockListEbayFilters  = {
            page: this.pageIndex + 1,
            size: this.pageSize,
            prix_min: this.filters.prix_min || 0,
            type: this.filters.type || 1
        };

        this.apiCall$?.unsubscribe();
        this.apiCall$ = this.stockService.getListEbay(formatedFilter).subscribe({
            next: (data) => {
                this.stockListEbays = data.results;
                this.resultsLength = data.total;
                this.sortEntries();
            },
            error: (err) => {
                this.isLoadingResults = false;
                console.error(err);
            },
            complete: () => {
                this.isLoadingResults = false;
                this.apiCall$?.unsubscribe();
            }
        })
    }

    onLazyLoad(event: TableLazyLoadEvent) {
        this.pageIndex = event.first! / event.rows!;
        this.pageSize = event.rows!;
        this.getTableEntries();
    }

    onStateRestore(state: any) {
        // Called when dt table restores state from storage
        this.isLoadedFromStorage = true;
        try {
            if (state && state.first !== undefined && state.rows) {
                this.pageIndex = state.first / state.rows;
            }
        } catch (e) {
            // ignore, justepour évité des erreurs si le state n'est pas conforme
        }
    }

    onSelectChange(event: SelectChangeEvent) {
        this.onSaveFilters();
        this.getTableEntries();
    }

    onInput() {
        const prix = this.filters.prix_min || 0;
        this.onSaveFilters();
        this.prixSubject.next(prix)
    }

    onPriceChange(value: number) {
        this.getTableEntries();
    }

    onClear() {
        this.filters = {
            prix_min: 20,
            type: 1
        };
        this.onSaveFilters();
        this.getTableEntries();
    }

    onSaveFilters() {
        this.storageService.searchEbayFilter = this.filters;
    }
}
